import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { maitriApi } from '../api/modules';
import { StatusPill } from '../components/ui/StatusPill';
import { MaitriWorkerDrawer } from '../drawers/MaitriWorkerDrawer';
import { relativeTime } from '../lib/format';

export function MaitriPage() {
  const [status, setStatus] = useState<'any' | 'active' | 'inactive'>('any');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const summary = useQuery({ queryKey: ['maitri-summary'], queryFn: maitriApi.summary });
  const list = useQuery({
    queryKey: ['maitri-list', { status, page }],
    queryFn: () => maitriApi.list({ page, page_size: 25, status }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <Header breadcrumbs={['Modules', 'AIM-MAITRI']} />
      <div className="px-8 pt-7 pb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">Module M04</div>
          <h1 className="serif-display text-3xl text-navy-800 font-medium">AIM-MAITRI Network</h1>
          <p className="text-sm text-navy-800/60 mt-1">
            Artificial Insemination &amp; field worker management · UPLDB · Bharat Pashudhan sync
          </p>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Total workers</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data ? summary.data.total.toLocaleString('en-IN') : '…'}
            </div>
            <div className="text-[11px] text-navy-800/60 mt-1.5 font-numeric">
              <span className="text-green-700">{summary.data?.weekly_reporting_pct ?? '…'}%</span> reporting weekly
            </div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Services (30 days)</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data ? summary.data.services_30d.toLocaleString('en-IN') : '…'}
            </div>
            <div className="text-[11px] text-navy-800/60 mt-1.5 font-numeric">AI, vaccinations, treatments</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Inactive (28+ days)</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data ? summary.data.inactive.toLocaleString('en-IN') : '…'}
            </div>
            <div className="text-[11px] text-red-600 mt-1.5 font-numeric">flagged for AI follow-up</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Synced to Bharat Pashudhan</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data ? summary.data.synced_to_bharat_pashudhan.toLocaleString('en-IN') : '…'}
            </div>
            <div className="text-[11px] text-navy-800/60 mt-1.5 font-numeric">cumulative services</div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white border border-cream-200 rounded-sm p-4 flex items-center gap-3 text-xs">
          <span className="text-navy-800/60">Filter:</span>
          {(['any', 'active', 'inactive'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setStatus(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-sm transition ${status === s ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}
            >
              {s === 'any' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-cream-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-cream-100 text-xs text-navy-800/60">
            {list.data ? `${list.data.data.length} of ${list.data.pagination.total.toLocaleString('en-IN')} workers` : 'Loading…'}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50">
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">MAITRI ID</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Gram Panchayat</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">District</th>
                <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Services (30 d)</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Last activity</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading || !list.data ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-t border-cream-100"><td colSpan={7} className="px-3 py-3"><div className="h-3 bg-cream-100 rounded animate-pulse" /></td></tr>
                ))
              ) : (
                list.data.data.map((w) => (
                  <tr
                    key={w.id}
                    onClick={() => setSelected(w.id)}
                    className="border-t border-cream-100 hover:bg-cream-50 cursor-pointer"
                  >
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">{w.id}</td>
                    <td className="px-3 py-2.5 font-medium text-navy-800">{w.name}</td>
                    <td className="px-3 py-2.5 text-navy-800/80">{w.gp ?? '—'}</td>
                    <td className="px-3 py-2.5 text-navy-800/70">{w.district}</td>
                    <td className={`px-3 py-2.5 text-right font-numeric font-medium ${w.services_30d === 0 ? 'text-red-600' : w.services_30d < 5 ? 'text-amber-600' : 'text-navy-800'}`}>
                      {w.services_30d}
                    </td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs">
                      {w.last_service_at ? relativeTime(w.last_service_at) : '—'}
                    </td>
                    <td className="px-3 py-2.5"><StatusPill status={w.active ? 'active' : 'inactive'} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {list.data && list.data.pagination.total_pages > 1 && (
            <div className="px-5 py-3 border-t border-cream-100 flex items-center justify-between text-xs">
              <span className="text-navy-800/60 font-numeric">
                Page {list.data.pagination.page} of {list.data.pagination.total_pages}
              </span>
              <div className="flex items-center gap-2">
                <button type="button" disabled={page === 1} onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1.5 bg-cream-100 text-navy-800/80 rounded-sm hover:bg-cream-200 disabled:opacity-50">← Previous</button>
                <button type="button" disabled={page >= list.data.pagination.total_pages} onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1.5 bg-navy-800 text-white rounded-sm hover:bg-navy-700 disabled:opacity-50">Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <MaitriWorkerDrawer open={!!selected} code={selected} onClose={() => setSelected(null)} />
    </>
  );
}
